import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { PawPrint, Heart, BookOpen, Users, Mail, FileText } from 'lucide-react';
import { client } from '../lib/sanity';
import { getPetUrlPath } from '../lib/petUtils';
import type { Pet } from '../types/sanity';

interface SitemapSection {
  title: string;
  icon: React.ReactNode;
  links: {
    title: string;
    path: string;
    description?: string;
  }[];
}

const SITEMAP_SECTIONS: SitemapSection[] = [
  {
    title: 'Adopcja',
    icon: <Heart className="w-6 h-6 text-accessible-red-600" />,
    links: [
      {
        title: 'Proces adopcji',
        path: '/adoptuj',
        description: 'Dowiedz się jak adoptować zwierzaka'
      },
      {
        title: 'Zwierzęta do adopcji',
        path: '/zwierzeta',
        description: 'Zobacz wszystkie zwierzęta czekające na dom'
      },
      {
        title: 'Adopcja wirtualna',
        path: '/wirtualna-adopcja',
        description: 'Wesprzyj zwierzaka nie zabierając go do domu'
      },
      {
        title: 'Dom tymczasowy',
        path: '/dom-tymczasowy',
        description: 'Zostań domem tymczasowym dla potrzebującego zwierzaka'
      }
    ]
  },
  {
    title: 'Aktualności',
    icon: <BookOpen className="w-6 h-6 text-accessible-red-600" />,
    links: [
      {
        title: 'Blog',
        path: '/aktualnosci',
        description: 'Najnowsze informacje z życia fundacji'
      },
      {
        title: 'Historie adopcyjne',
        path: '/historie-adopcyjne',
        description: 'Poznaj historie naszych podopiecznych w nowych domach' 
      },
      {
        title: 'Za tęczowym mostem',
        path: '/za-teczowym-mostem',
        description: 'Wspomnienie o naszych podopiecznych, którzy odeszli'
      }
    ]
  },
  {
    title: 'Wsparcie',
    icon: <Heart className="w-6 h-6 text-accessible-red-600" />,
    links: [
      {
        title: 'Jak pomóc',
        path: '/wsparcie',
        description: 'Różne formy wsparcia fundacji'
      },
      {
        title: 'Wpłata jednorazowa',
        path: '/wplata-jednorazowa',
        description: 'Wesprzyj nas jednorazową wpłatą - BLIK, PayPal, przelew'
      },
      {
        title: 'Przekaż 1.5% podatku',
        path: '/przekaz-podatek',
        description: 'Wesprzyj nas swoim podatkiem'
      },
      {
        title: 'Wolontariat',
        path: '/wolontariat',
        description: 'Dołącz do naszego zespołu wolontariuszy'
      }
    ]
  },
  {
    title: 'O nas',
    icon: <Users className="w-6 h-6 text-accessible-red-600" />,
    links: [
      {
        title: 'O fundacji',
        path: '/o-nas',
        description: 'Poznaj naszą historię i misję'
      },
      {
        title: 'Kontakt',
        path: '/kontakt',
        description: 'Dane kontaktowe i lokalizacja'
      }
    ]
  },
  {
    title: 'Dokumenty',
    icon: <FileText className="w-6 h-6 text-accessible-red-600" />,
    links: [
      {
        title: 'Sprawozdania',
        path: '/sprawozdania',
        description: 'Sprawozdania finansowe fundacji'
      },
      {
        title: 'Dofinansowania',
        path: '/dofinansowania',
        description: 'Informacje o projektach współfinansowanych'
      },
      {
        title: 'Statut',
        path: '/statut',
        description: 'Statut fundacji'
      },
      {
        title: 'Regulamin',
        path: '/regulamin',
        description: 'Regulamin fundacji'
      },
      {
        title: 'Polityka prywatności',
        path: '/polityka-prywatnosci',
        description: 'Informacje o przetwarzaniu danych osobowych'
      },
      {
        title: 'Polityka ciasteczek',
        path: '/polityka-cookies',
        description: 'Informacje o plikach cookies'
      },
      {
        title: 'Deklaracja dostępności',
        path: '/deklaracja-dostepnosci',
        description: 'Informacje o dostępności strony'
      }
    ]
  }
];

interface SitemapPost {
  _id: string;
  title: string;
  slug: {
    current: string;
  };
  publishedAt: string;
}

export function SitemapPage() {
  const [pets, setPets] = React.useState<Pet[]>([]);
  const [posts, setPosts] = React.useState<SitemapPost[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchDynamicContent = async () => {
      try {
        // Fetch pets
        const petsData = await client.fetch<Pet[]>(`
          *[_type == "pet" && status != "deceased" && !(_id in path("drafts.**"))] | order(name asc) {
            _id,
            name,
            slug,
            status
          }
        `);
        setPets(petsData || []);

        // Fetch posts
        const postsData = await client.fetch<SitemapPost[]>(`
          *[_type == "post" && !(_id in path("drafts.**"))] | order(publishedAt desc) {
            _id,
            title,
            slug,
            publishedAt
          }
        `);
        setPosts(postsData || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dynamic content:', error);
        setLoading(false);
      }
    };

    fetchDynamicContent();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <Helmet>
        <title>Mapa strony | Fundacja "Znajdki"</title>
        <meta name="description" content="Mapa strony Fundacji Znajdki. Znajdź szybko potrzebne informacje." />
      </Helmet>

      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-accessible-red-600/10 rounded-full mb-6">
          <PawPrint className="w-8 h-8 text-accessible-red-600" />
        </div>
        <h1 className="heading-1 mb-4">
          Mapa strony
        </h1>
        <p className="body-large max-w-2xl mx-auto">
          Wszystkie podstrony w jednym miejscu
        </p>
        <div className="mt-4 body-small">
          <p>Dla wyszukiwarek:</p>
          <a 
            href="/sitemap.xml"
            className="text-accessible-red-600 hover:text-accessible-red-700 transition-colors body-small no-margin"
            title="Zobacz mapę strony w formacie XML"
          >
            sitemap.xml
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {SITEMAP_SECTIONS.map((section) => (
          <div key={section.title} className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-6 rounded-lg shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              {section.icon}
              <h2 className="heading-4">
                {section.title}
              </h2>
            </div>
            <ul className="space-y-4">
              {section.links.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    title={link.description}
                    className="block p-3 rounded-lg hover:bg-accessible-gray-50 dark:hover:bg-accessible-gray-700 transition-colors high-contrast:hover:bg-white high-contrast:hover:text-black"
                  >
                    <span className="block label-large mb-1">
                      {link.title}
                    </span>
                    {link.description && (
                      <span className="body-small no-margin">
                        {link.description}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Dynamiczne zwierzęta */}
      {pets.length > 0 && (
        <div className="mt-12 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-6 rounded-lg shadow-lg">
          <div className="flex items-center gap-3 mb-6">
            <PawPrint className="w-6 h-6 text-accessible-red-600" />
            <h2 className="heading-4">
              Zwierzęta do adopcji ({pets.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {pets.map((pet) => (
              <Link
                key={pet._id}
                to={getPetUrlPath(pet)}
                className="block p-3 rounded-lg hover:bg-accessible-gray-50 dark:hover:bg-accessible-gray-700 transition-colors high-contrast:hover:bg-white high-contrast:hover:text-black border border-accessible-gray-100 dark:border-accessible-gray-700"
              >
                <span className="body-small">
                  {pet.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Dynamiczne posty */}
      {posts.length > 0 && (
        <div className="mt-8 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-6 rounded-lg shadow-lg">
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="w-6 h-6 text-accessible-red-600" />
            <h2 className="heading-4">
              Posty blogowe ({posts.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {posts.map((post) => (
              <Link
                key={post._id}
                to={`/aktualnosci/${post.slug.current}`}
                className="block p-3 rounded-lg hover:bg-accessible-gray-50 dark:hover:bg-accessible-gray-700 transition-colors high-contrast:hover:bg-white high-contrast:hover:text-black border border-accessible-gray-100 dark:border-accessible-gray-700"
              >
                <span className="body-small block truncate">
                  {post.title}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-12 bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-8 rounded-lg">
        <div className="flex items-center gap-4 mb-6">
          <Mail className="w-6 h-6 text-accessible-red-600" />
          <h2 className="heading-4 mb-2">
            Potrzebujesz pomocy?
          </h2>
        </div>
        <p className="body-medium mb-4">
          Nie możesz znaleźć potrzebnych informacji? Skontaktuj się z nami:
        </p>
        <div className="flex flex-wrap gap-4 mb-6">
          <a 
            href="tel:+48664821160"
            title="Zadzwoń do nas: 664 821 160"
            className="inline-flex items-center gap-2 px-6 py-3 bg-accessible-red-600 text-white rounded-lg hover:bg-accessible-red-700 transition-colors"
          >
            664 821 160
          </a>
          <a 
            href="mailto:znajdki2012@gmail.com"
            title="Napisz do nas: znajdki2012@gmail.com"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-accessible-gray-800 text-accessible-gray-900 dark:text-white rounded-lg hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-600 transition-colors border border-accessible-gray-200 dark:border-accessible-gray-600"
          >
            znajdki2012@gmail.com
          </a>
        </div>
      </div>
    </div>
  );
}